import { Component, computed, OnDestroy, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';

import { NotificationListComponent } from '../shared/components/notification-list/notification-list.component';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { AuthenticationService } from '@sb-tools/typescript/authentication/authentication.service';
import { SignalRService } from '../core/services/signalR.service';
import { NotificationService } from '../core/services/notification.service';
import { NotificationType } from '../core/enums/notification-type.enum';
import { UnreadNotificationsCountDTO } from '../core/models/dto/unread-notifications-count.dto';
import { TranslateModule } from '@ngx-translate/core';
import { SettingsMenuComponent } from '../../sb-modules/Frontend-Settings-Menu-Module/settings-menu.component';
import { UiSwitchModule } from 'ngx-ui-switch';
import { FormsModule } from '@angular/forms';
import { NotificationTypeNamePipe } from '../shared/pipes/notification-type-name.pipe';

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    imports: [
        TranslateModule,
        SettingsMenuComponent,
        UiSwitchModule,
        FormsModule,
        NotificationListComponent,
        NotificationTypeNamePipe,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild(NotificationListComponent) notificationListComponent: NotificationListComponent;

    isShowUnread = true;
    notificationType: NotificationType = NotificationType.none;
    notificationTypesToShow: NotificationType[] = [NotificationType.none];

    notificationFilterList: NotificationType[] = [
        NotificationType.none,
        NotificationType.mentionInNote,
        NotificationType.answerToNote,
        NotificationType.reply,
        NotificationType.subReply,
        NotificationType.ticketReminder,
        NotificationType.ticketSurveyResponseAdded,
    ];

    unreadNotificationsCountsData: WritableSignal<UnreadNotificationsCountDTO[]> = signal(null);
    unreadNotificationsCounts: Record<string, Signal<number>>;

    private unsubscriber = new Subject<void>();

    constructor(
        public authenticationService: AuthenticationService,
        public commonDataService: CommonDataService,
        private signalRService: SignalRService,
        private notificationService: NotificationService,
    ) {}

    ngOnInit(): void {
        this.initUnreadNotificationsCounts();
        this.loadUnreadNotificationsCountsData();

        this.signalRService
            .onUnreadNotificationCountUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.loadUnreadNotificationsCountsData();
            });
    }

    onTabClick(type: NotificationType): void {
        this.notificationType = type;
        if (type === NotificationType.ticketReminder) {
            this.notificationTypesToShow = [NotificationType.ticketReminder, NotificationType.replyReminder];
        } else {
            this.notificationTypesToShow = [type];
        }
    }

    private initUnreadNotificationsCounts(): void {
        this.unreadNotificationsCounts = this.notificationFilterList.reduce((res, key) => {
            res[key] = computed(() => {
                const counts = this.unreadNotificationsCountsData();

                if (!counts) {
                    return null;
                }

                if (key === NotificationType.none) {
                    return counts.find((item) => item.type === null && item.groupType === null)?.count;
                } else if (key === NotificationType.ticketReminder) {
                    const ticketCountData = counts.find((item) => item.type === NotificationType.ticketReminder);
                    const replyCountData = counts.find((item) => item.type === NotificationType.replyReminder);
                    return (ticketCountData?.count || 0) + (replyCountData?.count || 0);
                } else {
                    return counts.find((item) => item.type === key)?.count;
                }
            });
            return res;
        }, {});
    }

    private loadUnreadNotificationsCountsData(): void {
        this.notificationService
            .getUnreadCountsForGroups()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((result) => {
                this.unreadNotificationsCountsData.set(result);
            });
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
